import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import type { BookingData } from "../BookingModal";
import { TurnstileCaptcha } from "@/components/TurnstileCaptcha";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { EthicsCharter } from "@/components/EthicsCharter";
import { WelcomeQuestionnaire, validateQuestionnaireResponses } from "./WelcomeQuestionnaire";
import { getQuestionnaireType } from "@/data/welcomeQuestionnaireData";

// Validation schema for booking form
const bookingSchema = z.object({
  firstName: z.string().trim().min(1, "Prénom requis").max(50, "Prénom trop long (max 50 caractères)"),
  lastName: z.string().trim().min(1, "Nom requis").max(50, "Nom trop long (max 50 caractères)"),
  email: z.string().trim().email("Email invalide").max(255, "Email trop long"),
  phone: z
    .string()
    .trim()
    .min(1, "Téléphone requis")
    .max(20, "Téléphone trop long")
    .regex(/^[0-9+\-\s()]*$/, "Format de téléphone invalide"),
  additionalNotes: z.string().max(2000, "Notes trop longues (max 2000 caractères)").optional(),
});

interface PatientFormProps {
  bookingData: BookingData;
  setBookingData: (data: BookingData) => void;
  service: { title: string; duration: number | string };
  isOnline: boolean;
  eventId?: string;
  onSuccess: (appointmentId: string, childFirstName?: string, practitionerPhone?: string) => void;
}

export function PatientForm({ bookingData, setBookingData, service, isOnline, eventId, onSuccess }: PatientFormProps) {
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState<Record<string, any>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data with zod
    const validationResult = bookingSchema.safeParse({
      firstName: bookingData.firstName,
      lastName: bookingData.lastName,
      email: bookingData.email,
      phone: bookingData.phone,
      additionalNotes: bookingData.additionalNotes,
    });

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    // For events, we don't need date/time validation
    if (!eventId && (!bookingData.date || !bookingData.time)) {
      toast.error("Veuillez sélectionner une date et une heure");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Veuillez accepter les conditions d'utilisation");
      return;
    }

    if (!captchaToken) {
      toast.error("Veuillez compléter la vérification de sécurité");
      return;
    }

    // Validate questionnaire responses for non-event bookings
    if (!eventId) {
      const questionnaireValidation = validateQuestionnaireResponses(service.title, questionnaireResponses);
      if (!questionnaireValidation.valid) {
        toast.error("Veuillez compléter le questionnaire de bienvenue", {
          description: `Champs manquants: ${questionnaireValidation.missingFields.slice(0, 2).join(', ')}...`,
        });
        return;
      }
    }

    // Validate birth date if provided
    let birthDateStr = null;
    if (birthDay || birthMonth || birthYear) {
      if (!birthDay || !birthMonth || !birthYear) {
        toast.error("Veuillez compléter la date de naissance (jour, mois et année)");
        return;
      }
      const day = parseInt(birthDay);
      const month = parseInt(birthMonth);
      const year = parseInt(birthYear);

      if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
        toast.error("Date de naissance invalide");
        return;
      }

      birthDateStr = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    }

    setLoading(true);

    try {
      // For events, create a registration instead of an appointment
      if (eventId) {
        // Get event details first
        const { data: eventData, error: eventError } = await supabase
          .from("events")
          .select("*")
          .eq("id", eventId)
          .single();

        if (eventError) {
          console.error("Event fetch error:", eventError);
          throw eventError;
        }

        // Generate registration ID client-side to avoid SELECT after INSERT (RLS blocks SELECT for public users)
        const registrationId = crypto.randomUUID();

        // Create event registration - don't use .select() as RLS blocks SELECT for public users
        const { error: regError } = await supabase.from("event_registrations").insert({
          id: registrationId,
          event_id: eventId,
          first_name: bookingData.firstName,
          last_name: bookingData.lastName,
          email: bookingData.email,
          phone: bookingData.phone,
        });

        if (regError) {
          console.error("Registration error:", regError);
          throw regError;
        }

        // Create registration object for subsequent operations
        const registration = {
          id: registrationId,
          event_id: eventId,
          first_name: bookingData.firstName,
          last_name: bookingData.lastName,
          email: bookingData.email,
          phone: bookingData.phone,
        };

        // Decrement available spots using the SECURITY DEFINER function (works for unauthenticated users)
        if (eventData && eventData.available_spots && eventData.available_spots > 0) {
          const { error: decrementError } = await supabase.rpc("decrement_event_spots", {
            event_id: eventId,
          });

          if (decrementError) {
            console.error("Error decrementing event spots:", decrementError);
          }

          // Update the appointment notes with participant count using SECURITY DEFINER function
          const { error: notesError } = await supabase.rpc("update_event_appointment_notes", {
            p_event_id: eventId,
          });

          if (notesError) {
            console.error("Error updating appointment notes:", notesError);
          }
        }

        // Send confirmation email for event with CAPTCHA token
        const { error: emailError } = await supabase.functions.invoke("send-event-confirmation", {
          body: {
            registration: {
              ...registration,
              event_title: eventData.title,
              event_date: eventData.event_date,
              event_format: eventData.format,
              event_duration: eventData.duration,
              event_price: eventData.price,
            },
            captchaToken,
          },
        });

        if (emailError) {
          console.error("Error sending email:", emailError);
          toast.warning("Inscription confirmée mais erreur lors de l'envoi de l'email");
        } else {
          toast.success("Inscription confirmée !");
        }

        onSuccess(registration.id);
      } else {
        // For regular appointments
        if (!bookingData.date || !bookingData.time) {
          throw new Error("Date et heure requises");
        }

        const [hours, minutes] = bookingData.time.split(":").map(Number);
        const appointmentDateTime = new Date(bookingData.date);
        appointmentDateTime.setHours(hours, minutes, 0, 0);

        // Validate availability before creating appointment
        const dayOfWeek = bookingData.date.getDay();
        const dateStr = `${bookingData.date.getFullYear()}-${String(bookingData.date.getMonth() + 1).padStart(2, "0")}-${String(bookingData.date.getDate()).padStart(2, "0")}`;

        // Get practitioner user_id
        const { data: practitioners } = await supabase.from("patients").select("user_id").limit(1).single();

        if (practitioners?.user_id) {
          // Use RPC function for secure public access to availability
          const { data: weeklyOverrides } = await supabase
            .rpc("get_public_availability_for_date", { p_date: dateStr });

          let isSlotAvailable = false;

          if (weeklyOverrides && weeklyOverrides.length > 0) {
            // Check if time is within any override slot
            const availableOverrides = weeklyOverrides.filter((o) => {
              if (!o.is_available) return false;
              if (isOnline && o.in_office_only) return false;
              if (!isOnline && o.online_only) return false;

              const slotHour = parseInt(bookingData.time.split(":")[0]);
              const startHour = parseInt(o.start_time.split(":")[0]);
              const endHour = parseInt(o.end_time.split(":")[0]);

              return slotHour >= startHour && slotHour < endHour;
            });

            isSlotAvailable = availableOverrides.length > 0;
          } else {
            // Check default availability settings
            const { data: availabilitySettings } = await supabase
              .from("availability_settings")
              .select("*")
              .eq("user_id", practitioners.user_id)
              .eq("day_of_week", dayOfWeek)
              .eq("is_available", true);

            if (availabilitySettings) {
              const compatibleSettings = availabilitySettings.filter((setting) => {
                if (isOnline && setting.in_office_only) return false;
                if (!isOnline && setting.online_only) return false;

                const slotHour = parseInt(bookingData.time.split(":")[0]);
                const startHour = parseInt(setting.start_time.split(":")[0]);
                const endHour = parseInt(setting.end_time.split(":")[0]);

                return slotHour >= startHour && slotHour < endHour;
              });

              isSlotAvailable = compatibleSettings.length > 0;
            }
          }

          if (!isSlotAvailable) {
            throw new Error("Ce créneau n'est pas disponible selon les horaires d'ouverture");
          }
        }

        // Generate appointment ID client-side to avoid SELECT after INSERT (RLS blocks SELECT for public users)
        const appointmentId = crypto.randomUUID();

        // Get questionnaire type for storage
        const questionnaireType = getQuestionnaireType(service.title);

        // Determine if this is a child appointment and prepare child data
        const isChildAppointment = 
          questionnaireType === "naturopathy_children" || 
          (questionnaireType === "bach_flowers" && questionnaireResponses.consultation_pour?.includes('enfant'));

        // For child appointments, use the child's birth date for statistics
        let appointmentBirthDate = birthDateStr;
        let appointmentGender = gender || null;
        
        if (isChildAppointment && questionnaireResponses.date_naissance_enfant) {
          const childDob = questionnaireResponses.date_naissance_enfant;
          if (childDob.day && childDob.month && childDob.year) {
            appointmentBirthDate = `${childDob.year}-${String(childDob.month).padStart(2, "0")}-${String(childDob.day).padStart(2, "0")}`;
          }
          // Also use child's gender if available
          if (questionnaireResponses.genre_biologique_enfant) {
            appointmentGender = questionnaireResponses.genre_biologique_enfant;
          }
        }

        const appointmentData = {
          id: appointmentId,
          service_type: service.title,
          appointment_date: appointmentDateTime.toISOString(),
          appointment_time: bookingData.time,
          duration_minutes: typeof service.duration === "number" ? service.duration : 60,
          is_online: isOnline,
          first_name: bookingData.firstName,
          last_name: bookingData.lastName,
          email: bookingData.email,
          phone: bookingData.phone,
          birth_date: appointmentBirthDate, // Use child's birth date for child appointments
          gender: appointmentGender, // Use child's gender for child appointments
          consultation_reasons: [], // No longer using the old reasons system
          additional_notes: bookingData.additionalNotes,
          status: "confirmed",
          event_id: null,
          // Location for cabinet appointments
          location_id: !isOnline ? (bookingData.locationId || null) : null,
        };

        // Create appointment - don't use .select() as RLS blocks SELECT for public users
        const { error: aptError } = await supabase.from("appointments").insert(appointmentData as any);

        if (aptError) {
          console.error("Appointment creation error:", aptError);
          throw aptError;
        }

        // Save questionnaire responses
        const questionnaireId = crypto.randomUUID();
        const { error: questionnaireError } = await supabase.from("welcome_questionnaires").insert({
          id: questionnaireId,
          appointment_id: appointmentId,
          questionnaire_type: questionnaireType,
          responses: questionnaireResponses,
        });

        if (questionnaireError) {
          console.error("Questionnaire save error:", questionnaireError);
          // Don't fail the whole booking if questionnaire save fails
        }

        // Create/Update consultant file and link it to the appointment immediately.
        // Guarantees the consultant file exists even if the email function is
        // rate-limited or fails, and keeps the parent's name up-to-date.
        try {
          const { error: upsertError } = await supabase.rpc("upsert_patient_and_link_appointment", {
            p_appointment_id: appointmentId,
            p_email: bookingData.email,
            p_first_name: bookingData.firstName,
            p_last_name: bookingData.lastName,
            p_phone: bookingData.phone,
            p_birth_date: birthDateStr,
            p_gender: gender || null,
          });
          if (upsertError) {
            console.error("upsert_patient_and_link_appointment error:", upsertError);
          }
        } catch (e) {
          console.error("upsert_patient_and_link_appointment exception:", e);
        }

        // Create appointment object for email function
        const appointment = {
          id: appointmentId,
          ...appointmentData,
        };

        // Use the already-determined isChildAppointment variable from above
        const childFirstNameForEmail = isChildAppointment ? questionnaireResponses.prenom_enfant : undefined;

        // Send confirmation email with CAPTCHA token
        // Include parent's own birth date separately for parent file creation
        // Include location_id for cabinet appointments
        const { error: emailError } = await supabase.functions.invoke("send-booking-confirmation", {
          body: {
            appointment: {
              ...appointment,
              service_title: service.title,
              patient_name: `${bookingData.firstName} ${bookingData.lastName}`,
              gender: appointmentGender,
              child_first_name: childFirstNameForEmail,
              // Always pass parent's own birth date and gender for the consultant file
              parent_birth_date: birthDateStr,
              parent_gender: gender || null,
              // Location ID for cabinet appointments (snapshot will be created in the edge function)
              location_id: !isOnline ? (bookingData.locationId || null) : null,
            },
            captchaToken,
          },
        });

        if (emailError) {
          console.error("Error sending email:", emailError);
          toast.warning("Rendez-vous créé mais erreur lors de l'envoi de l'email");
        } else {
          toast.success("Rendez-vous confirmé !");
        }

        // For "Naturopathie Enfants & Ados" or "Fleurs de Bach" (for child), create the child record.
        // Rationale: for a brand new consultant, the patient record may be created/linked during
        // the confirmation backend process; retry a few times to avoid race conditions.
        let childName: string | undefined = undefined;

        if (isChildAppointment) {
          const childFirstName = questionnaireResponses.prenom_enfant;
          childName = childFirstName;
          const childBirthDate = questionnaireResponses.date_naissance_enfant;
          const childMedicalHistory = questionnaireResponses.antecedents_medicaux || null;
          const childAdditionalInfo = questionnaireResponses.informations_complementaires || null;

          if (childFirstName && childBirthDate?.day && childBirthDate?.month && childBirthDate?.year) {
            const childDob = `${childBirthDate.year}-${String(childBirthDate.month).padStart(2, "0")}-${String(childBirthDate.day).padStart(2, "0")}`;

            for (let attempt = 1; attempt <= 3; attempt++) {
              const { data: childId, error: childError } = await supabase.rpc("create_child_from_booking", {
                p_parent_email: bookingData.email,
                p_child_name: childFirstName,
                p_child_dob: childDob,
                p_medical_history: childMedicalHistory,
                p_additional_info: childAdditionalInfo,
              });

              if (!childError && childId) {
                console.log("Child created successfully with ID:", childId);
                break;
              }

              if (childError) {
                console.error(`Child creation error (attempt ${attempt}/3):`, childError);
              }

              // Small delay before retry (handles eventual consistency / async linkage)
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }
        }

        // Fetch practitioner phone from app_settings
        let practitionerPhone: string | undefined = undefined;
        try {
          const { data: settingsData } = await supabase.rpc("get_all_app_settings");
          if (settingsData && typeof settingsData === "object") {
            practitionerPhone = (settingsData as any).practitioner_phone || undefined;
          }
        } catch (e) {
          console.warn("Could not fetch practitioner phone:", e);
        }

        onSuccess(appointment.id, childName, practitionerPhone);
      }
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(`Erreur: ${error.message || "Erreur lors de la réservation"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{eventId ? "Réservez votre place" : "Indiquez vos informations"}</h2>
        {eventId && (
          <p className="text-muted-foreground">
            Complétez vos informations pour confirmer votre réservation à cet événement
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Prénom *</Label>
          <Input
            id="firstName"
            required
            value={bookingData.firstName}
            onChange={(e) => setBookingData({ ...bookingData, firstName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Nom *</Label>
          <Input
            id="lastName"
            required
            value={bookingData.lastName}
            onChange={(e) => setBookingData({ ...bookingData, lastName: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mail *</Label>
        <Input
          id="email"
          type="email"
          required
          value={bookingData.email}
          onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Téléphone *</Label>
        <Input
          id="phone"
          type="tel"
          required
          value={bookingData.phone}
          onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
        />
      </div>

      {!eventId && (
        <div className="space-y-2">
          <Label>Genre biologique</Label>
          <Select value={gender || "none"} onValueChange={(value) => setGender(value === "none" ? "" : value)}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sélectionner...</SelectItem>
              <SelectItem value="femme">Femme</SelectItem>
              <SelectItem value="homme">Homme</SelectItem>
              <SelectItem value="intersexe">Intersexe</SelectItem>
              <SelectItem value="prefere_ne_pas_repondre">Préfère ne pas répondre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!eventId && (
        <div className="space-y-2">
          <Label>Date de naissance</Label>
          <div className="grid grid-cols-3 gap-2">
            <Input
              type="number"
              placeholder="Jour"
              min="1"
              max="31"
              value={birthDay}
              onChange={(e) => setBirthDay(e.target.value)}
            />
            <Input
              type="number"
              placeholder="Mois"
              min="1"
              max="12"
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value)}
            />
            <Input
              type="number"
              placeholder="Année"
              min="1900"
              max={new Date().getFullYear()}
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Welcome Questionnaire - replaces old consultation reasons */}
      {!eventId && (
        <WelcomeQuestionnaire
          serviceTitle={service.title}
          responses={questionnaireResponses}
          onResponsesChange={setQuestionnaireResponses}
        />
      )}

      {!eventId && (
        <div className="space-y-2">
          <Label htmlFor="notes">Informations complémentaires (facultatif)</Label>
          <Textarea
            id="notes"
            rows={3}
            placeholder="Ajoutez des informations complémentaires si nécessaire..."
            value={bookingData.additionalNotes}
            onChange={(e) => setBookingData({ ...bookingData, additionalNotes: e.target.value })}
          />
        </div>
      )}

      <div className="flex items-start space-x-2">
        <Checkbox
          id="terms"
          checked={acceptedTerms}
          onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
          required
        />
        <label htmlFor="terms" className="text-sm leading-relaxed">
          En poursuivant, vous confirmez que vous avez lu et accepté la{" "}
          <PrivacyPolicy triggerClassName="text-primary hover:underline inline cursor-pointer">
            <span className="text-primary hover:underline cursor-pointer">Politique de confidentialité</span>
          </PrivacyPolicy>{" "}
          et la{" "}
          <EthicsCharter triggerClassName="text-primary hover:underline inline cursor-pointer">
            <span className="text-primary hover:underline cursor-pointer">Charte déontologique</span>
          </EthicsCharter>
          .
        </label>
      </div>

      <TurnstileCaptcha
        onVerify={(token) => setCaptchaToken(token)}
        onExpire={() => setCaptchaToken(null)}
        onError={() => setCaptchaToken(null)}
        theme="light"
      />

      <Button
        type="submit"
        disabled={loading || !acceptedTerms || !captchaToken}
        className="w-full bg-primary hover:bg-primary/90"
      >
        {loading ? "Confirmation en cours..." : eventId ? "Confirmer l'événement" : "Confirmer le rendez-vous"}
      </Button>
    </form>
  );
}
